import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Star, TrendingUp, TrendingDown } from 'lucide-react'
import { Button } from '@mui/material'

import { makeRequest } from '../../core/services/v1/request'
import Config from '../../core/config/'
import LoaderUnitic from '../../Component/Loader/LoaderUnitic'
import { showPairNameUsdt } from '../../core/helper/date-format'
import CoinLogo from './CoinLogo'
import Sparkline from './Sparkline'
import './MarketPage.css'
import './MarketRedesign.css'

const DEFAULT_LIMIT = 10
const LIMIT_OPTIONS = [5, 10, 25, 50, 100]
const FAV_KEY = 'unitic_market_favorites'
const REFRESH_MS = 15000;
const SPARK_REFRESH_MS = 60000

const FUTURES_TRADE_PATH = (symbol) => `/futures/trade/${symbol}`

const emptyPagination = (limit, page = 1) => ({
  page,
  limit,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  nextPage: null,
  previousPage: null,
})

const loadFavorites = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(FAV_KEY) || '[]')
    return new Set(raw.map((k) => (String(k).includes(':') ? k : `spot:${k}`)))
  } catch (e) {
    return new Set()
  }
}

function formatNumber(num = 0) {
  num = Number(num)
  if (!num) return 0

  const suffixes = ['', 'K', 'M', 'B', 'T']
  let suffixIndex = 0
  let formatted = num

  while (formatted >= 1000 && suffixIndex < suffixes.length - 1) {
    formatted /= 1000
    suffixIndex++
  }

  return `${formatted.toFixed(2)}${suffixes[suffixIndex]}`
}

const ChangeText = ({ value }) => {
  const v = Number.isFinite(value) ? value : 0
  return (
    <span className={`mk-change ${v >= 0 ? 'mk-up' : 'mk-down'}`}>
      {v >= 0 ? '+' : ''}
      {v.toFixed(2)}%
    </span>
  )
}

const ChangeChip = ({ value }) => {
  const v = Number.isFinite(value) ? value : 0
  const up = v >= 0
  return (
    <span className={`mk-chip ${up ? 'up' : 'down'}`}>
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {up ? '+' : ''}
      {v.toFixed(2)}%
    </span>
  )
}

const PairName = ({ row }) => (
  <div className='mk-pair-name'>
    <span className='mk-pair-base'>{row.base}</span>
    <span className='mk-pair-quote'>{row.market === 'perp' ? 'USDT' : '/USDT'}</span>
    {row.market === 'perp' && <span className='mk-tag'>Perp</span>}
  </div>
)

const UTI = ({ marketType = 'usdt', search = '' }) => {
  const navigate = useNavigate()
  const tableRef = useRef(null)
  const reqId = useRef(0)
  const prevQuery = useRef({ marketType, search })

  const [markets, setMarkets] = useState([])
  const [loader, setLoader] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [pagination, setPagination] = useState(emptyPagination(DEFAULT_LIMIT))
  const [favorites, setFavorites] = useState(loadFavorites)
  const [sparks, setSparks] = useState({})

  const favoritesRef = useRef(favorites)
  favoritesRef.current = favorites

  async function fetchMarkets(silent = false) {
    const id = ++reqId.current

    try {
      if (!silent) setLoader(true)

      let url

      if (marketType === 'favorites') {
        const spot = []
        const perp = []
        favoritesRef.current.forEach((key) => {
          const [market, symbol] = key.split(':')
          if (!symbol) return
          if (market === 'perp') perp.push(symbol)
          else spot.push(symbol)
        })

        if (!spot.length && !perp.length) {
          setMarkets([])
          return
        }

        url =
          `${Config.V3_API_URL}trade/getFavoriteMarkets?` +
          `spot=${spot.join(',')}&perp=${perp.join(',')}`
      } else {
        const query = new URLSearchParams({
          search: search || '',
          page: String(page),
          limit: String(limit),
        })

        url =
          `${Config.V3_API_URL}trade/` +
          (marketType === 'futures' ? 'getMarketsFutures' : 'getMarketsUSDTV2') +
          `?${query.toString()}`
      }

      const response = await makeRequest({ url, method: 'GET' })
      if (id !== reqId.current) return

      if (response?.status) {
        const defaultMarket = marketType === 'futures' ? 'perp' : 'spot'

        setMarkets(
          (response.data || []).map((item) => ({
            ...item,
            market: item.market || defaultMarket,
          })),
        )

        if (marketType !== 'favorites') {
          setPagination(response.pagination || emptyPagination(limit, page))
        }
      } else if (!silent) {
        setMarkets([])
        setPagination(emptyPagination(limit))
      }
    } catch (err) {
      console.log('fetchMarkets error:', err)
      if (!silent && id === reqId.current) setMarkets([])
    } finally {
      if (id === reqId.current) setLoader(false)
    }
  }

  useEffect(() => {
    const changed =
      prevQuery.current.marketType !== marketType ||
      prevQuery.current.search !== search

    prevQuery.current = { marketType, search }

    if (changed && page !== 1) {
      setPage(1)
      return
    }

    fetchMarkets(false)
  }, [marketType, search, page, limit])

  useEffect(() => {
    if (!REFRESH_MS) return undefined

    const interval = setInterval(() => {
      if (!document.hidden) fetchMarkets(true)
    }, REFRESH_MS)

    return () => clearInterval(interval)
  }, [marketType, search, page, limit])

  const toRow = (data) => {
    const market = data.market
    const symbol = data?.symbol
    const pct = Number(data?.price24hPcnt || 0)
    const base = data?.baseCoin || String(symbol || '').replace(/USDT$/, '')

    return {
      key: `${market}:${symbol}`,
      favKey: `${market}:${symbol}`,
      symbol,
      market,
      category: market === 'perp' ? 'linear' : 'spot',
      base,
      image: data?.image,
      price: data?.lastPrice,
      priceClass:
        Number(data?.lastPrice) > 0
          ? pct > 0
            ? 'blinkGreenColor'
            : 'blinkRedColor'
          : '',
      change: pct * 100,
      high: data?.highPrice24h,
      low: data?.lowPrice24h,
      volume: formatNumber(Number(data?.turnover24h)),
      onTrade: () =>
        market === 'perp'
          ? navigate(FUTURES_TRADE_PATH(symbol))
          : navigate('/spot-usdt/' + showPairNameUsdt(symbol, '-')),
    }
  }

  let rows = (Array.isArray(markets) ? markets : []).map(toRow)

  if (marketType === 'favorites') {
    const q = (search || '').toLowerCase()
    rows = rows.filter(
      (r) => favorites.has(r.favKey) && (!q || r.symbol.toLowerCase().includes(q)),
    )
  }

  const hasMarkets = rows.length > 0

  const sparkKey = rows.map((r) => `${r.category}:${r.symbol}`).join(',')

  useEffect(() => {
    if (!sparkKey) return undefined

    let alive = true

    const load = async () => {
      const groups = {}
      sparkKey.split(',').forEach((k) => {
        const [category, symbol] = k.split(':')
        if (!groups[category]) groups[category] = []
        groups[category].push(symbol)
      })

      await Promise.all(
        Object.entries(groups).map(async ([category, symbols]) => {
          try {
            const res = await makeRequest({
              url:
                `${Config.V3_API_URL}trade/getSparklines?` +
                `category=${category}&symbols=${symbols.join(',')}`,
              method: 'GET',
            })

            if (alive && res?.status && res.data) {
              setSparks((prev) => {
                const next = { ...prev }
                Object.entries(res.data).forEach(([symbol, points]) => {
                  next[`${category}:${symbol}`] = points
                })
                return next
              })
            }
          } catch (e) {
          }
        }),
      )
    }

    load()
    const interval = setInterval(load, SPARK_REFRESH_MS)

    return () => {
      alive = false
      clearInterval(interval)
    }
  }, [sparkKey])

  const toggleFav = (key) => {
    setFavorites((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify([...next]))
      } catch (e) {
        // ignore
      }
      return next
    })
  }

  const goToPage = (p) => {
    setPage(p)
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value))
    setPage(1)
  }

  const getPaginationPages = (currentPage, totalPages) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages]
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages]
  }

  const showPagination = marketType !== 'favorites' && pagination.total > 0

  const FavButton = ({ row, size = 16 }) => (
    <button
      type='button'
      className={`mk-fav ${favorites.has(row.favKey) ? 'active' : ''}`}
      aria-label='Favorite'
      onClick={() => toggleFav(row.favKey)}
    >
      <Star size={size} />
    </button>
  )

  return (
    <div className='mk-card mk-table-card' ref={tableRef}>
      {loader && (
        <div className='mk-state'>
          <LoaderUnitic width={80} />
        </div>
      )}

      {!loader && !hasMarkets && (
        <div className='mk-state'>
          <div className='text-center'>
            {marketType === 'favorites' ? (
              <>
                <Star size={40} className='mk-empty-star' />
                <p className='mk-empty-title'>No favorites yet</p>
                <p className='mk-muted mb-0'>
                  Tap the star next to any market to add it here.
                </p>
              </>
            ) : (
              <>
                <img className='no-record-image' src='/assets/no-re.png' alt='no-record' />
                <br />
                <span className='mk-muted'>No Records Found</span>
              </>
            )}
          </div>
        </div>
      )}

      {!loader && hasMarkets && (
        <>
          <div className='mk-table-wrap d-none d-md-block'>
            <table className='mk-table'>
              <colgroup>
                <col className='mk-w-star' />
                <col className='mk-w-coin' />
                <col className='mk-w-data' />
                <col className='mk-w-data' />
                <col className='mk-w-data' />
                <col className='mk-w-data' />
                <col className='mk-w-data' />
                <col className='mk-w-data' />
                <col className='mk-w-data' />
              </colgroup>
              <thead>
                <tr>
                  <th className='mk-col-star' />
                  <th className='mk-left'>Trading Pairs</th>
                  <th>Last Price</th>
                  <th>24h Change</th>
                  <th>24h High</th>
                  <th>24h Low</th>
                  <th>24h Turnover</th>
                  <th className='mk-col-chart-column mk-col-center-column'>Chart</th>
                  <th className='mk-col-center-column'>Trade</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td className='mk-col-star'>
                      <FavButton row={row} />
                    </td>

                    <td className='mk-left'>
                      <div
                        className='mk-pair'
                        role='button'
                        tabIndex={0}
                        onClick={row.onTrade}
                        onKeyDown={(e) => e.key === 'Enter' && row.onTrade()}
                      >
                        <CoinLogo src={row.image} symbol={row.base} size={32} />
                        <PairName row={row} />
                      </div>
                    </td>

                    <td className={`mk-price ${row.priceClass}`}>{row.price}</td>
                    <td>
                      <ChangeText value={row.change} />
                    </td>
                    <td className='mk-muted'>{row.high}</td>
                    <td className='mk-muted'>{row.low}</td>
                    <td className='mk-muted'>{row.volume}</td>
                    <td className='mk-col-chart'>
                      <Sparkline
                        points={sparks[`${row.category}:${row.symbol}`]}
                        up={row.change >= 0}
                      />
                    </td>
                    <td>
                      <Button className='mk-trade-btn' size='small' onClick={row.onTrade}>
                        Trade
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='mk-m-list d-md-none'>
            {rows.map((row) => (
              <div key={row.key} className='mk-m-card'>
                <div className='mk-m-top'>
                  <div className='mk-pair' role='button' tabIndex={0} onClick={row.onTrade}>
                    <CoinLogo src={row.image} symbol={row.base} size={34} />
                    <PairName row={row} />
                  </div>
                  <FavButton row={row} size={18} />
                </div>

                <div className='mk-m-price-row'>
                  <div>
                    <div className={`mk-m-price ${row.priceClass}`}>{row.price}</div>
                    <ChangeChip value={row.change} />
                  </div>
                  <Sparkline
                    points={sparks[`${row.category}:${row.symbol}`]}
                    up={row.change >= 0}
                    width={110}
                    height={40}
                  />
                </div>

                <div className='mk-m-grid'>
                  <div>
                    <p>24h High</p>
                    <span>{row.high}</span>
                  </div>
                  <div>
                    <p>24h Low</p>
                    <span>{row.low}</span>
                  </div>
                  <div>
                    <p>24h Turnover</p>
                    <span>{row.volume}</span>
                  </div>
                </div>

                <Button className='mk-trade-btn mk-trade-btn-block' onClick={row.onTrade}>
                  Trade
                </Button>
              </div>
            ))}
          </div>

          {showPagination && (
            <div className='mk-pagination-box'>
              <div className='market-pagination-wrapper'>
                <div className='market-pagination-info'>
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)}
                  {' - '}
                  {Math.min(page * limit, pagination.total)}
                  {' of '}
                  {pagination.total}
                </div>

                <div className='market-pagination'>
                  <button
                    type='button'
                    className='market-page-btn'
                    disabled={loader || !pagination.hasPreviousPage}
                    onClick={() => pagination.previousPage && goToPage(pagination.previousPage)}
                  >
                    ‹
                  </button>

                  {getPaginationPages(page, pagination.totalPages).map((item, index) => {
                    if (item === '...') {
                      return (
                        <span key={`dots-${index}`} className='market-page-dots'>
                          ...
                        </span>
                      )
                    }

                    return (
                      <button
                        key={item}
                        type='button'
                        className={`market-page-btn ${item === page ? 'market-page-btn-active' : ''}`}
                        disabled={loader}
                        onClick={() => goToPage(item)}
                      >
                        {item}
                      </button>
                    )
                  })}

                  <button
                    type='button'
                    className='market-page-btn'
                    disabled={loader || !pagination.hasNextPage}
                    onClick={() => pagination.nextPage && goToPage(pagination.nextPage)}
                  >
                    ›
                  </button>
                </div>

                <div className='market-page-limit'>
                  <span>Show:</span>
                  <select value={limit} onChange={handleLimitChange} disabled={loader}>
                    {LIMIT_OPTIONS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default UTI